/**
 * Плагины страницы кабинета — ядро механизма. Почва коворка `cowork-server-plugin-pages`,
 * переработана блоком l1 спринта `cabinet-layout`.
 *
 * МЕХАНИЗМ, А НЕ ЧАСТНЫЙ СЛУЧАЙ. Т5/Т8 шторма 22.08: правый сайдбар — свойство ВСЕХ модулей
 * кабинета, а не надстройка над журналом. Здесь нет ни слова про журнал: страница объявляет свой
 * список жильцов, ядро не знает, чья это страница.
 *
 * ЧИСТОЕ ЯДРО ОТДЕЛЬНО ОТ REACT. Оснастка кабинета тестирует логику (`*.test.ts`), а не рендер:
 * решения живут функциями, проверяемыми без DOM, а компоненты остаются тонкими.
 *
 * ВКЛЮЧЁННОСТЬ — ОПЕРАЦИЯ РЕЕСТРА, НЕ ПОЛЕ ОПИСАНИЯ (M5′). `CabinetPagePlugin` описывает жильца и
 * поля `enabled` не несёт; включённость живёт в состоянии страницы и меняется операцией.
 *
 * ── ВКЛЮЧЁННОСТЬ И ЕСТЬ ПОКАЗ (23.08, слово владельца) ───────────────────────────────────────
 *
 * Прежде это были РАЗНЫЕ вещи: галочка говорила дому «зови этого плагина», отдельный клик по
 * названию — «покажи виджет». Разведение было намеренным.
 *
 * Владелец снял его словом: «клик по названию — лишнее действие, активной галочки должно быть
 * достаточно». Довод подкреплён случаем: 22.08 он не смог найти показ — плагин был включён, а
 * виджета не было, и это читалось как поломка.
 *
 * ЧТО ОСТАЛОСЬ: дом по-прежнему ВЛАДЕЕТ включённостью, M5′ не отменён — изменилось лишь то, что
 * страница из неё выводит. Выключенный по-прежнему не зовётся домом.
 * ЧТО УШЛО: `activeId` и понятие «активный жилец». Включённые рисуются СТОПКОЙ, один под другим —
 * гнездо перестало быть единственным.
 *
 * ── СВОРАЧИВАНИЕ СТАЛО САМОСТОЯТЕЛЬНЫМ (23.08, там же) ───────────────────────────────────────
 *
 * Правило «свернуть основной блок нельзя, пока нет виджета» СНЯТО. Оно держалось на том, что
 * сворачивать имеет смысл только ради виджета; владелец хочет сворачивать список сам по себе, и
 * кнопка встаёт над списком (блок l2). Правило отменено СЛОВОМ, а не сломалось: зуб на него
 * переписан, а не удалён молча.
 *
 * Файл принадлежит зоне l1, поэтому снятие правила сделано здесь, хотя названо в l2.
 */
import type { ReactNode } from 'react';

/** Форма показа виджета. Словарь взят из `DisplayForm` контрактов, включая лазейку `x-…`. */
export type PageWidgetForm =
  | 'row'
  | 'table'
  | 'zone-map'
  | 'histogram'
  | 'time-series'
  | `x-${string}`;

/**
 * Жилец страницы. Настройки и виджет — РАЗНЫЕ вещи и рисуются в разных местах:
 * настройки в сайдбаре (канон §3), виджет под основным блоком.
 */
export interface CabinetPagePlugin {
  readonly id: string;
  readonly name: string;
  readonly description?: string;
  readonly form: PageWidgetForm;
  /** Панель настроек. Рисуется ТОЛЬКО в сайдбаре; дублировать её в теле страницы запрещено. */
  readonly renderSettings?: () => ReactNode;
  /** Виджет. Рисуется ПОД основным блоком страницы. */
  readonly renderWidget: () => ReactNode;
}

/**
 * Состояние плагинов страницы. Одно на страницу; чужие страницы о нём не знают.
 *
 * `activeId` здесь НЕТ намеренно: включённость и есть показ.
 */
export interface PagePluginsState {
  /** Кто включён. Отсутствие id — выключен: реестр говорит о включённых, а не о всех. */
  readonly enabled: readonly string[];
  /** Свёрнут ли основной блок. От жильцов НЕ зависит — сворачивание самостоятельно. */
  readonly mainCollapsed: boolean;
}

export const initialPagePluginsState: PagePluginsState = {
  enabled: [],
  mainCollapsed: false,
};

export const isEnabled = (state: PagePluginsState, id: string): boolean =>
  state.enabled.includes(id);

/**
 * Включить или выключить жильца.
 *
 * Выключение просто убирает его из показа — гасить нечего: отдельного «активного» больше нет.
 * Свёрнутость основного блока при этом НЕ трогается: человек свернул список для себя, и разворачивать
 * его из-за чужого выключателя значило бы отменять чужое решение.
 */
export function setEnabled(state: PagePluginsState, id: string, enabled: boolean): PagePluginsState {
  const next = enabled
    ? state.enabled.includes(id)
      ? state.enabled
      : [...state.enabled, id]
    : state.enabled.filter((x) => x !== id);
  return { ...state, enabled: next };
}

/** Свернуть или развернуть основной блок. Условий нет: сворачивание — самостоятельное желание. */
export function setMainCollapsed(state: PagePluginsState, collapsed: boolean): PagePluginsState {
  return { ...state, mainCollapsed: collapsed };
}

/** Жильцы, которых страница показывает в сайдбаре: все объявленные, в порядке объявления. */
export function visiblePlugins(
  plugins: readonly CabinetPagePlugin[],
): readonly CabinetPagePlugin[] {
  return plugins;
}

/**
 * Жильцы, чьи виджеты рисуются — все включённые, СТОПКОЙ, в порядке объявления страницей.
 *
 * Порядок объявления, а не порядок включения: человек включает и выключает по многу раз, и список
 * не должен перетасовываться от этого. Порядок объявления назначает страница, и он устойчив.
 */
export function shownPlugins(
  plugins: readonly CabinetPagePlugin[],
  state: PagePluginsState,
): readonly CabinetPagePlugin[] {
  return plugins.filter((p) => isEnabled(state, p.id));
}

/** Формы, которые страница умеет показать. Форма вне списка — не отказ, а честная заглушка. */
export const SUPPORTED_FORMS: readonly PageWidgetForm[] = ['row', 'table', 'histogram', 'time-series'];

export function isSupportedForm(form: PageWidgetForm): boolean {
  return SUPPORTED_FORMS.includes(form);
}
